import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateReport } from '@/hooks/useReports';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';

const REPORT_REASONS = [
  'Spam',
  'Harassment',
  'Inappropriate Content',
  'Impersonation',
  'Other',
];

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportedUserId?: string;
  reportedPostId?: string;
  reportedName?: string;
}

export function ReportDialog({
  open,
  onOpenChange,
  reportedUserId,
  reportedPostId,
  reportedName,
}: ReportDialogProps) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const createReport = useCreateReport();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!reason) return;

    try {
      await createReport.mutateAsync({
        reportedUserId,
        reportedPostId,
        reason,
        details: details.trim() || undefined,
      });

      toast({
        title: 'Report submitted',
        description: 'Thank you. Our team will review this report.',
      });

      setReason('');
      setDetails('');
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Report {reportedPostId ? 'Post' : reportedName || 'User'}
          </DialogTitle>
          <DialogDescription>
            Help us keep QBN safe. Select a reason and provide any additional context.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Details (optional)</Label>
            <Textarea
              placeholder="Provide any additional context..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason || createReport.isPending}
            variant="destructive"
          >
            {createReport.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
